import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import $ from 'jquery';
import * as action from '../Action/UserSegments/UserSegments_Action';
import Model from "./Model/Index";
import NumberDisplay from "./NumberDisplay/Index";
import { usePermission } from "../hooks/usePermission";
import { PERM } from "../config/permissions";
import AuditTrail, { AuditTrailStyles } from "./AuditTrail";
import { parseUserIds } from "../utils/seriesAudience";

const PAGE_SIZE = 20;
// `loaded` stays false until an edited segment's IDs arrive — saving earlier would replace them with an empty list.
const EMPTY_FORM = { id: 0, name: '', userIds: [], pasted: '', series: [], loaded: true };

const SegmentStyles = () => (
    <style>{`
        .segment-ids { display: flex; flex-wrap: wrap; gap: 6px; max-height: 180px; overflow-y: auto; }
        .segment-id-chip {
            display: inline-flex; align-items: center; gap: 6px; font-size: 12px;
            background: var(--glass-bg); border: 1px solid var(--border-color);
            border-radius: 12px; padding: 3px 10px;
        }
        .segment-id-chip button { border: none; background: transparent; color: var(--error-color); padding: 0; line-height: 1; }
        .segment-series-list { margin: 0; padding-left: 18px; font-size: 13px; }
    `}</style>
);

const errorMessage = (err) => err?.responseMessage || err?.message || 'Something went wrong. Please try again.';

// Admin → User Segments: reusable named lists of user IDs (users.login_type_id)
// that a series' "Specific users" audience is built from.
const UserSegments = () => {
    const { canEdit, canDelete } = usePermission();
    const allowEdit = canEdit(PERM.USER_SEGMENTS);
    const allowDelete = canDelete(PERM.USER_SEGMENTS);
    const dispatch = useDispatch();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ show: false, title: 'Add' });
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const listRequestRef = useRef(0);

    const loadSegments = useCallback(() => {
        const requestId = ++listRequestRef.current;
        setLoading(true);
        dispatch(action.getUserSegments({ page, per_page: PAGE_SIZE, search: search.trim() }))
            .then((res) => {
                if (requestId !== listRequestRef.current) return;
                const list = res?.responseDetails || [];
                setRows(Array.from(list));
                setTotal(list.totalRecords || 0);
            })
            .catch((err) => {
                if (requestId === listRequestRef.current) toast.error(errorMessage(err));
            })
            .finally(() => {
                if (requestId === listRequestRef.current) setLoading(false);
            });
    }, [dispatch, page, search]);

    useEffect(() => {
        const timer = setTimeout(loadSegments, 300);
        return () => clearTimeout(timer);
    }, [loadSegments]);

    const openModal = (title) => {
        setModal({ show: true, title });
        $("body").addClass("no_scroll");
        $('.overlay').addClass('qv_active');
    };

    const closeModal = () => {
        setModal({ show: false, title: 'Add' });
        $("body").removeClass("no_scroll");
        $('.overlay').removeClass('qv_active');
        setForm(EMPTY_FORM);
        setErrors({});
    };

    const openSegment = (row) => {
        openModal(allowEdit ? 'Edit' : 'View');
        setForm({ ...EMPTY_FORM, id: row.id, name: row.name, loaded: false });
        dispatch(action.getUserSegmentDetails({ id: row.id }))
            .then((res) => {
                const d = res?.responseDetails || {};
                setForm(prev => ({ ...prev, userIds: (d.user_ids || []).map(String), series: d.series || [], loaded: true }));
            })
            .catch((err) => toast.error(errorMessage(err)));
    };

    const readCsv = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        file.text()
            .then(text => setForm(prev => ({ ...prev, pasted: prev.pasted ? `${prev.pasted}\n${text}` : text })))
            .catch(() => toast.error('Could not read the CSV file.'));
    };

    const removeUserId = (id) => setForm(prev => ({ ...prev, userIds: prev.userIds.filter(x => x !== id) }));

    const save = (e) => {
        e.preventDefault();
        if (saving || !allowEdit || !form.loaded) return;
        const name = form.name.trim();
        if (!name) {
            setErrors({ name: 'Please enter a segment name' });
            return;
        }
        const userIds = [...new Set([...form.userIds, ...parseUserIds(form.pasted)])];
        const isEdit = form.id !== 0;
        setSaving(true);
        dispatch(isEdit
            ? action.updateUserSegment({ id: form.id, name, user_ids: userIds })
            : action.addUserSegment({ name, user_ids: userIds }))
            .then((res) => {
                const unmatched = res?.responseDetails?.warnings?.unmatched_user_ids || [];
                if (unmatched.length) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Saved with warnings',
                        html: `${unmatched.length} user ID(s) match no user. They were saved but give no access:<br/><small>${unmatched.map(id => $('<div>').text(id).html()).join(', ')}</small>`,
                        customClass: { confirmButton: 'btn' },
                    });
                } else {
                    toast.success(res?.responseMessage || 'User segment saved');
                }
                closeModal();
                loadSegments();
            })
            .catch((err) => toast.error(errorMessage(err)))
            .finally(() => setSaving(false));
    };

    const remove = (row) => {
        if (!allowDelete) return;
        Swal.fire({
            title: `Delete "${row.name}"?`,
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--first-color)',
            cancelButtonColor: 'var(--error-color)',
            confirmButtonText: 'Yes, delete it!',
            customClass: { confirmButton: 'btn', cancelButton: 'btn' },
        }).then((result) => {
            if (!result.isConfirmed) return;
            dispatch(action.deleteUserSegment({ id: row.id }))
                .then(() => {
                    toast.success('User segment deleted');
                    loadSegments();
                })
                // Blocked while an active series uses it — the message names those series.
                .catch((err) => Swal.fire({ icon: 'error', title: 'Cannot delete', text: errorMessage(err), customClass: { confirmButton: 'btn' } }));
        });
    };

    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const readOnly = !allowEdit;

    return (
        <>
            <AuditTrailStyles />
            <SegmentStyles />
            <div className='overlay'></div>
            <section className="section">
                <div className="card">
                    <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <h4 className="m-0">User Segments (<span className="total_user"><NumberDisplay count={total} /></span>)</h4>
                        <div className="d-flex align-items-center gap-2">
                            <input type="search" aria-label="Search segments" placeholder="Search" value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                            {allowEdit && <button type="button" className="btn upgrade-btn" onClick={() => openModal('Add')}>Add Segment</button>}
                        </div>
                    </div>
                    <div className="card-body">
                        <table className="user-table table table-striped w-100">
                            <thead>
                                <tr><th>Name</th><th>Users</th><th>Series using it</th><th>Last edited</th><th>Action</th></tr>
                            </thead>
                            <tbody>
                                {loading && !rows.length ? (
                                    <tr><td colSpan={5} className="text-center">Loading segments...</td></tr>
                                ) : rows.length ? rows.map(row => (
                                    <tr key={row.id}>
                                        <td>{row.name}</td>
                                        <td>{row.user_count}</td>
                                        <td>{row.series_count}</td>
                                        <td><AuditTrail record={row} /></td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <button type="button" className="btn btn-sm btn-primary" title={allowEdit ? 'Edit' : 'View'} onClick={() => openSegment(row)}>
                                                    <i className={allowEdit ? 'fas fa-edit' : 'fas fa-eye'}></i>
                                                </button>
                                                {allowDelete && (
                                                    <button type="button" className="btn btn-sm btn-danger" title="Delete" onClick={() => remove(row)}>
                                                        <i className="fas fa-trash-alt"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={5} className="text-center">No user segments yet</td></tr>
                                )}
                            </tbody>
                        </table>
                        {pageCount > 1 && (
                            <div className="d-flex align-items-center justify-content-end gap-2 mt-3">
                                <button type="button" className="btn btn-sm" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>Previous</button>
                                <span>Page {page} of {pageCount}</span>
                                <button type="button" className="btn btn-sm" disabled={page >= pageCount || loading} onClick={() => setPage(p => p + 1)}>Next</button>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {modal.show && (
                <Model className="common-popup add-genre-popup active">
                    <div className="edit-heading d-flex align-items-center justify-content-between">
                        <h3>{modal.title} User Segment</h3>
                        <button type="button" className="common-close edit-close-btn" aria-label="Close" onClick={closeModal}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    <div className="edit-inner">
                        <form onSubmit={save}>
                            <div className="form-group">
                                <label htmlFor="segment_name">Name</label>
                                <input type="text" id="segment_name" maxLength={100} value={form.name} disabled={readOnly}
                                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. QC Team" />
                                <span className='text-danger pt-2'>{errors.name}</span>
                            </div>
                            <div className="form-group">
                                <label>User IDs ({form.userIds.length})</label>
                                {form.userIds.length ? (
                                    <div className="segment-ids">
                                        {form.userIds.map(id => (
                                            <span className="segment-id-chip" key={id}>
                                                {id}
                                                {!readOnly && <button type="button" aria-label={`Remove ${id}`} onClick={() => removeUserId(id)}><i className="fas fa-times"></i></button>}
                                            </span>
                                        ))}
                                    </div>
                                ) : <small>No user IDs yet.</small>}
                            </div>
                            {!readOnly && (
                                <div className="form-group">
                                    <label htmlFor="segment_paste">Add user IDs</label>
                                    <textarea id="segment_paste" rows={4} value={form.pasted}
                                        onChange={(e) => setForm(prev => ({ ...prev, pasted: e.target.value }))}
                                        placeholder="Paste user IDs separated by commas or new lines" />
                                    <div className="d-flex align-items-center gap-2 mt-2">
                                        <label className="btn btn-sm mb-0" htmlFor="segment_csv">Upload CSV</label>
                                        <input type="file" id="segment_csv" accept=".csv,text/csv,text/plain" className="d-none" onChange={readCsv} />
                                        <small>Duplicates are removed. IDs that match no user are saved but give no access.</small>
                                    </div>
                                </div>
                            )}
                            {form.id !== 0 && (
                                <div className="form-group">
                                    <label>Series using this segment</label>
                                    {form.series.length ? (
                                        <ul className="segment-series-list">
                                            {form.series.map(s => <li key={s.id}>{s.title} ({s.status})</li>)}
                                        </ul>
                                    ) : <small>Not used by any series.</small>}
                                </div>
                            )}
                            {!readOnly && (
                                <div className="action-footer">
                                    <button type="submit" className="btn" disabled={saving || !form.loaded}>{saving ? 'Saving...' : 'Save'}</button>
                                </div>
                            )}
                        </form>
                    </div>
                </Model>
            )}
        </>
    );
};

export default UserSegments;
